import React, { useEffect, useState } from 'react';
import { Loader2, Mail, Printer, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const blobToB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

// Send an order sheet to the doctor for signature by email link and/or fax; tracked on the Order Signatures page
export const SendOrderSignModal = ({ patientId, orderType, docType, defaults = {}, getPdfBlob, onClose }) => {
  const [name, setName] = useState(defaults.physicianName || '');
  const [email, setEmail] = useState(defaults.physicianEmail || '');
  const [fax, setFax] = useState(defaults.physicianFax || '');
  const [channels, setChannels] = useState({ email: true, fax: Boolean(defaults.physicianFax) });
  const [faxLive, setFaxLive] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/order-sign/config').then(r => setFaxLive(r.data.faxLive)).catch(() => {}); }, []);
  // fill the doctor's details from the chart (saved there after the first send)
  useEffect(() => {
    if (!patientId) return;
    api.get(`/patients/${patientId}`).then(r => {
      const p = r.data.patient || r.data;
      setName(x => x || p.physicianSigningName || p.primaryPhysician || '');
      setEmail(x => x || p.physicianEmail || p.primaryPhysicianEmail || '');
      setFax(x => { const f = x || p.physicianFax || p.primaryPhysicianFax || ''; if (f) setChannels(c => ({ ...c, fax: true })); return f; });
    }).catch(() => {});
  }, [patientId]);
  const picked = Object.keys(channels).filter(k => channels[k]);
  const ready = name.trim().length > 1 && picked.length && (!channels.email || email.includes('@')) && (!channels.fax || fax.replace(/\D/g, '').length >= 10);
  const send = async () => {
    setBusy(true);
    try {
      const pdfBase64 = await blobToB64(await getPdfBlob());
      const r = await api.post('/order-sign/send', { patientId, orderType, docType, pdfBase64, physicianName: name, physicianEmail: channels.email ? email : undefined,
        physicianFax: channels.fax ? fax : undefined, channels: picked });
      toast.success(r.data.message);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the order');
    } finally { setBusy(false); }
  };
  const box = 'w-full px-3 py-2 border rounded-lg text-sm';
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="order-sign-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gray-900 p-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37]">Send {orderType} for Signature</h3>
          <button onClick={() => onClose(false)} className="text-white/80 hover:text-white" data-testid="order-sign-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Doctor's name" className={box} data-testid="order-sign-physician" />
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={channels.email} onChange={e => setChannels({ ...channels, email: e.target.checked })} data-testid="order-sign-ch-email" /><Mail className="w-4 h-4" /> Email a signing link</label>
          {channels.email && <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="doctor@clinic.com" className={box} data-testid="order-sign-email" />}
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={channels.fax} onChange={e => setChannels({ ...channels, fax: e.target.checked })} data-testid="order-sign-ch-fax" /><Printer className="w-4 h-4" /> Fax the order</label>
          {channels.fax && <input value={fax} onChange={e => setFax(e.target.value)} placeholder="Doctor's fax number" className={box} data-testid="order-sign-fax" />}
          {channels.fax && !faxLive && <p className="text-xs text-amber-700 bg-amber-50 rounded p-2" data-testid="order-sign-fax-demo">Fax service is not connected yet: the fax is logged as DEMO and not really sent. The email link works.</p>}
          <p className="text-xs text-gray-500">The order counts as overdue if it is not signed within 7 days. You can resend it from Order Signatures.</p>
          <button onClick={send} disabled={busy || !ready} className="w-full py-2.5 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:bg-gray-300 flex items-center justify-center gap-2" data-testid="order-sign-send">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send for Signature
          </button>
        </div>
      </div>
    </div>
  );
};
